import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FJActionAIVideoGenerator from '../../../../redux/action/FJActionAIVideoGenerator.js';
import FJAIVideoMultiModeImageUploader from '../view/FJAIVideoMultiModeImageUploader.jsx';
import FJConstant from '../../../../constants/FJConstant.js';
import { setSliceIsDraggingFileAction } from '../../../../redux/slices/FJSliceCommon.js';
import FJActionAiHistoryCommon from '../../../../redux/action/FJActionAiHistoryCommon.js';
import FJActionEditPage from '../../../../redux/action/FJActionEditPage.js';
import {
    AI_VIDEO_GENERATION_MODEL_LIST,
    IMAGE_UPLOAD_MODES,
    MODEL_VALUE_MAP,
} from '../../../../config/dataConfig/FJAIVideoGeneratorData.js';

const FJAIVideoMultiModeImageUploaderContainer = () => {
    const dispatch = useDispatch();

    // Redux状态
    const imageMode = useSelector(state => state.AIVideoGenerator.imageMode);
    const uploadImages = useSelector(state => state.AIVideoGenerator.uploadImages);
    const uploadStatus = useSelector(state => state.AIVideoGenerator.uploadStatus);
    const currentModel = useSelector(state => state.AIVideoGenerator.currentModel);
    const motionControlImage = useSelector(state => state.AIVideoGenerator.motionControlImage);
    const motionControlVideo = useSelector(state => state.AIVideoGenerator.motionControlVideo);
    const curCharacterOrientation = useSelector(state => state.AIVideoGenerator.curCharacterOrientation);

    // 从common slice获取拖拽状态
    const isDraggingFile = useSelector(state => state.common.isDraggingFile);

    // 处理函数
    const setIsDraggingFile = value => {
        dispatch(setSliceIsDraggingFileAction(value));
    };

    const showAIImageHistoryList = () => {
        dispatch(FJActionAiHistoryCommon.showAIImageHistoryListAction());
    };

    const openMediaLibrary = index => {
        window.mediaLibraryOptions = {
            addIndex: index,
        };
        dispatch(FJActionEditPage.openMediaLibraryAction());
    };

    // 根据模式从uploadImages中获取对应的图片
    const currentImages = useMemo(() => {
        // const validImages = uploadImages.filter(img => img && img.src);
        const modelConfig = AI_VIDEO_GENERATION_MODEL_LIST.find(i => i.value === currentModel);
        const supportReferenceImages = modelConfig?.supportReferenceImages || 4;
        const validImages = uploadImages.slice(0, supportReferenceImages);
        if (imageMode === IMAGE_UPLOAD_MODES.SINGLE) {
            return {
                originImage: validImages[0] || {},
                uploadStatus: validImages[0]?.src ? FJConstant.SUCCESS : FJConstant.DEFAULT,
            };
        } else if (imageMode === IMAGE_UPLOAD_MODES.START_END) {
            return {
                startImage: validImages[0] || {},
                endImage: validImages[1] || {},
                startUploadStatus: validImages[0]?.src ? FJConstant.SUCCESS : FJConstant.DEFAULT,
                endUploadStatus: validImages[1]?.src ? FJConstant.SUCCESS : FJConstant.DEFAULT,
            };
        } else if (imageMode === IMAGE_UPLOAD_MODES.MULTI) {
            return {
                multiImages: validImages,
                multiUploadStatuses: validImages.map(img => (img?.src ? FJConstant.SUCCESS : FJConstant.DEFAULT)),
            };
        }
        return {};
    }, [uploadImages, imageMode, currentModel]);
    const isMotionControl = useMemo(() => {
        return currentModel === MODEL_VALUE_MAP.KLING_V26_MOTION_CONTROL;
    }, [currentModel]);
    const maxMotionVideoDuration = useMemo(() => {
        if (isMotionControl) {
            const modelConfig = AI_VIDEO_GENERATION_MODEL_LIST.find(i => i.value === currentModel);
            return modelConfig?.characterOrientation?.find(i => i.value === curCharacterOrientation)?.maxDuration || 30;
        }
    }, [isMotionControl, curCharacterOrientation, currentModel]);

    // 切换图片模式
    const handleImageModeChange = useCallback(
        mode => {
            dispatch(FJActionAIVideoGenerator.changeImageModeAction(mode));
        },
        [dispatch],
    );

    // 单图模式 - 文件上传
    const handleSingleImageUpload = async file => {
        dispatch(FJActionAIVideoGenerator.uploadFileAction(file, 0));
    };

    // 单图模式 - 文件删除
    const handleSingleImageDelete = () => {
        dispatch(FJActionAIVideoGenerator.removeUploadImageAction(0));
    };

    // 首尾帧模式 - 首帧上传
    const handleStartImageUpload = async file => {
        dispatch(FJActionAIVideoGenerator.uploadFileAction(file, 0));
    };

    // 首尾帧模式 - 尾帧上传
    const handleEndImageUpload = async file => {
        dispatch(FJActionAIVideoGenerator.uploadFileAction(file, 1));
    };

    // 首尾帧模式 - 删除首帧
    const handleStartImageDelete = () => {
        dispatch(FJActionAIVideoGenerator.removeUploadImageAction(0));
    };

    // 首尾帧模式 - 删除尾帧
    const handleEndImageDelete = () => {
        dispatch(FJActionAIVideoGenerator.removeUploadImageAction(1));
    };

    // 多图模式 - 图片上传
    const handleMultiImageUpload = async (file, index) => {
        dispatch(FJActionAIVideoGenerator.uploadFileAction(file, index));
    };

    // 多图模式 - 删除指定图片
    const handleMultiImageDelete = index => {
        dispatch(FJActionAIVideoGenerator.removeUploadImageAction(index));
    };

    // 交换首尾帧图片
    const handleExchangeStartEndImage = useCallback(() => {
        // 找到所有非空图片的索引

        const firstImg = currentImages.startImage;
        const secondImg = currentImages.endImage;

        dispatch(FJActionAIVideoGenerator.updateUploadMediaAction(0, secondImg));
        dispatch(FJActionAIVideoGenerator.updateUploadMediaAction(1, firstImg));
    }, [currentImages.endImage, currentImages.startImage, dispatch]);
    const handleUploadMotionControlImage = async file => {
        dispatch(FJActionAIVideoGenerator.uploadMotionControlImageAction(file));
    };
    const handleDeleteMotionControlImage = () => {
        dispatch(FJActionAIVideoGenerator.deleteMotionControlImageAction());
    };
    const handleUploadMotionControlVideo = async file => {
        dispatch(FJActionAIVideoGenerator.uploadMotionControlVideoAction(file));
    };
    const handleDeleteMotionControlVideo = () => {
        dispatch(FJActionAIVideoGenerator.deleteMotionControlVideoAction());
    };

    const props = {
        // 模式相关
        imageMode,
        onImageModeChange: handleImageModeChange,

        // 单图模式
        originImage: currentImages.originImage || {},
        uploadStatus: currentImages.uploadStatus || uploadStatus,
        uploadFile: handleSingleImageUpload,
        onFileDelete: handleSingleImageDelete,

        // 首尾帧模式
        startImage: currentImages.startImage || {},
        endImage: currentImages.endImage || {},
        onStartImageUpload: handleStartImageUpload,
        onEndImageUpload: handleEndImageUpload,
        onStartImageDelete: handleStartImageDelete,
        onEndImageDelete: handleEndImageDelete,
        startUploadStatus: currentImages.startUploadStatus || FJConstant.DEFAULT,
        endUploadStatus: currentImages.endUploadStatus || FJConstant.DEFAULT,
        onExchangeStartEndImage: handleExchangeStartEndImage,

        // 多图模式
        multiImages: currentImages.multiImages || uploadImages,
        onMultiImageUpload: handleMultiImageUpload,
        onMultiImageDelete: handleMultiImageDelete,
        multiUploadStatuses: currentImages.multiUploadStatuses || uploadImages.map(() => FJConstant.DEFAULT),

        // 公共props
        isDraggingFile,
        setIsDraggingFile,
        openMediaLibrary,
        showAIImageHistoryList,
        // 特殊模式
        isMotionControl,
        motionControlImage: motionControlImage || {},
        motionControlVideo: motionControlVideo || {},
        maxMotionVideoDuration,
        onUploadMotionControlImage: handleUploadMotionControlImage,
        onDeleteMotionControlVideo: handleDeleteMotionControlVideo,
        onDeleteMotionControlImage: handleDeleteMotionControlImage,
        onUploadMotionControlVideo: handleUploadMotionControlVideo,
    };

    return <FJAIVideoMultiModeImageUploader {...props} />;
};

export default FJAIVideoMultiModeImageUploaderContainer;
